import {
  CREW_PACKET_STATUS_LABELS,
  crewPacketLetter,
  crewPacketLink,
  crewPacketStatus,
  type Crew,
  type CrewPacketLanguage,
} from "@roof-hawk/core";
import { useState } from "react";

import { ErrorNote, Modal } from "../../components/ui";
import { t } from "../../i18n";
import {
  createCrewPacket,
  listCrewPackets,
  markCrewPacketSent,
} from "../../lib/crewPackets";
import { fmtDateTime } from "../../lib/format";
import { useAsync } from "../../lib/hooks";
import { fetchCompanySettings } from "../../lib/queries";
import { useSession } from "../../session";
import { ComposeEmail } from "../email/ComposeEmail";
import { useGmailAccount, useGmailSenders } from "../email/useGmailAccount";

/** The company's name until `company_settings` says otherwise (rule 11). */
const COMPANY_NAME_FALLBACK = "CopperRidge Roofing & Exteriors LLC";

/**
 * Sending a crew their subcontractor packet (ADR 0279).
 *
 * Jonathon, 2026-10-09: "we will email it to them, they will fill it out, sign
 * it and it will automatically be sent back. needs to be a spanish vs english
 * version."
 *
 * Two steps. First the language, because it decides the letter and which page
 * the link opens on, and choosing it is what makes the link; then the same
 * composer every other letter in Roof Board uses, so the From line, the Copy
 * button for a crew who would rather have it on WhatsApp, and the note a rep
 * wants to add all behave as they do everywhere else. Sending or copying the
 * letter is what marks the packet sent. The packets already sent
 * are listed under the choice, so nobody sends a second one to a crew that
 * signed yesterday without seeing it.
 */
export function SendCrewPacketModal({
  crew,
  onClose,
}: {
  crew: Crew;
  onClose: () => void;
}) {
  const { displayName } = useSession();
  const { account } = useGmailAccount();
  const { senders, own } = useGmailSenders();
  const [link, setLink] = useState<{
    language: CrewPacketLanguage;
    token: string;
    url: string;
  } | null>(null);
  const [making, setMaking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sent = useAsync(() => listCrewPackets(crew.id), [crew.id]);
  const settings = useAsync(() => fetchCompanySettings(), []);
  const company = settings.data?.name?.trim() || COMPANY_NAME_FALLBACK;

  async function choose(language: CrewPacketLanguage) {
    setMaking(true);
    setError(null);
    try {
      const token = await createCrewPacket({
        crewId: crew.id,
        language,
        sentTo: crew.email,
      });
      setLink({
        language,
        token,
        url: crewPacketLink(window.location.origin, token),
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setMaking(false);
    }
  }

  if (link) {
    const letter = crewPacketLetter({
      language: link.language,
      company,
      contactName: crew.contact_name,
      link: link.url,
      senderName: own?.name ?? displayName,
    });
    return (
      <ComposeEmail
        title={t("Send the packet to {name}", { name: crew.name })}
        subtitle={
          link.language === "es"
            ? t("In Spanish. They can switch to English on the page.")
            : t("In English. They can switch to Spanish on the page.")
        }
        to={crew.email ?? ""}
        subject={letter.subject}
        body={letter.body}
        // A crew is not a customer: this must not touch any customer's
        // follow-up clock or contact flags.
        internal
        connectedAs={account?.email_address ?? null}
        senders={senders}
        ownSender={own}
        sendLabel={t("Send packet")}
        extraFields={
          !crew.email ? (
            <div className="config-warn" role="status">
              {t(
                "{name} has no email address on file. Type one in below, or copy the message and text it to them.",
                { name: crew.name },
              )}
            </div>
          ) : null
        }
        // The packet counts as sent only once the letter goes, to the address
        // it actually carried; closing the composer leaves "Link made, not
        // sent" in the list below.
        onSent={({ to }) => markCrewPacketSent(link.token, to)}
        onCopied={({ to }) => markCrewPacketSent(link.token, to)}
        onClose={onClose}
      />
    );
  }

  const now = new Date();
  return (
    <Modal
      title={t("Subcontractor packet for {name}", { name: crew.name })}
      subtitle={t(
        "W-9, bank account, insurance and the subcontractor agreement — filled in and signed on their phone, and filed here when they sign.",
      )}
      onClose={onClose}
    >
      <ErrorNote error={error ?? settings.error} />
      <p className="na-hint">{t("Which language should it open in?")}</p>
      <div className="cpk-send-choice">
        <button
          className="btn"
          disabled={making}
          onClick={() => void choose("en")}
        >
          {t("English")}
        </button>
        <button
          className="btn"
          disabled={making}
          onClick={() => void choose("es")}
        >
          Español
        </button>
      </div>

      {sent.data && sent.data.length > 0 ? (
        <>
          <p className="na-hint">{t("Already made")}</p>
          <table className="etable">
            <tbody>
              {sent.data.map((packet) => {
                const status = crewPacketStatus(packet, now);
                return (
                  <tr key={packet.id}>
                    <td>{fmtDateTime(packet.created_at)}</td>
                    <td>
                      {packet.language === "es" ? t("Spanish") : t("English")}
                    </td>
                    <td className="sub">{packet.sent_to ?? "—"}</td>
                    <td>
                      {t(CREW_PACKET_STATUS_LABELS[status])}
                      {status === "signed" &&
                      packet.signer_name &&
                      packet.signed_at
                        ? t(" by {name}, {when} — the PDF is in Documents", {
                            name: packet.signer_name,
                            when: fmtDateTime(packet.signed_at),
                          })
                        : ""}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      ) : null}
    </Modal>
  );
}
